// Tagged transcript renderer with word-level audio sync.
//
// Two modes per detail item:
//
//   1. data.turns present  →  structured render with one <span> per word,
//      currentTimeMs drives a highlight class on the matching word, and
//      auto-scroll centers the active turn. Words are click-to-seek.
//
//   2. data.turns absent (legacy)  →  fall back to the inherited
//      pre-formatted text renderer (TurnBlock-based) that ConversationDetailPage
//      uses today, so old TAGGED detail items keep rendering until they're
//      re-tagged.
//
// The component is purposely stateless beyond a scroll-pause timer:
// `currentTimeMs` flows in from the parent (hoisted player state).

import React, { useEffect, useMemo, useRef } from "react";
import {
  Box,
  Flex,
  HStack,
  VStack,
  Text,
  Avatar,
  Badge,
  Divider,
} from "@chakra-ui/react";

// Extract a snippet id from a turn — checks the first word with a
// `source_snippet_id`. Returns `null` if no word in the turn carries one
// (older tagger output may omit this).
function snippetIdForTurn(turn) {
  const words = Array.isArray(turn?.words) ? turn.words : [];
  for (const w of words) {
    const sid = w?.source_snippet_id;
    if (sid != null) return sid;
  }
  // Some tagger variants stash it on the turn itself.
  return turn?.source_snippet_id ?? null;
}

// Dashed separator + label rendered when the snippet id changes between
// adjacent turns. Keeps the visual chrome consistent with the RAW tab.
const SnippetMarker = ({ snippetId }) => (
  <HStack
    spacing={2}
    align="center"
    data-testid="tagged-snippet-marker"
    data-snippet-id={snippetId ?? ""}
  >
    <Divider borderStyle="dashed" />
    <Badge variant="subtle" colorScheme="gray" fontSize="xx-small" flexShrink={0}>
      Snippet S{snippetId ?? "?"}
    </Badge>
    <Divider borderStyle="dashed" />
  </HStack>
);

const ROLE_COLOR = {
  Customer: "purple",
  Employee: "blue",
  Other: "gray",
};

const SCROLL_PAUSE_AFTER_USER_INTERACTION_MS = 5000;

function formatTimestamp(ms) {
  if (!Number.isFinite(ms) || ms < 0) return "0:00";
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

// Find the currently-active word across the flat turn->word list.
// Returns `{ turnIdx, wordIdx }` or `null` if `currentTimeMs` precedes
// the first word / follows the last word of the last turn.
//
// `epochOffset` is subtracted from every word/turn start_ms / end_ms
// before comparison — the tagger inherits epoch ms from the RAW word
// stream (e.g. 1779395170308), but the player's `currentTimeMs` is
// conv-relative (e.g. 12000 for 12s into playback). Without the
// subtraction nothing ever brackets the active time.
//
// STICKY BRACKETING: previously this used strict inclusive bracketing
// (`currentTimeMs >= word.start_ms && currentTimeMs <= word.end_ms`),
// which meant any tick landing in the small gap between consecutive
// words returned `null` and the highlight flickered off. That gap is
// unavoidable when WaveSurfer's `timeupdate` only fires ~4 times per
// second — words shorter than 250ms get skipped entirely. The sticky
// rule is "the last word whose start_ms <= currentTimeMs, as long as
// currentTimeMs hasn't run past the last word's end_ms of the last
// turn we considered". This guarantees every tick that lands within
// the conversation maps to exactly one word.
function findActiveWord(turns, currentTimeMs, epochOffset = 0) {
  if (!Array.isArray(turns) || !Number.isFinite(currentTimeMs)) return null;
  const off = Number(epochOffset) || 0;
  let best = null;
  for (let ti = 0; ti < turns.length; ti += 1) {
    const turn = turns[ti];
    const words = Array.isArray(turn?.words) ? turn.words : [];
    if (words.length === 0) continue;
    // If the cursor is before this turn's first word, stop — earlier
    // turns are already past, no later turn can win.
    const firstStart = Number(words[0]?.start_ms);
    if (Number.isFinite(firstStart) && currentTimeMs < firstStart - off) {
      break;
    }
    // Pick the last word in this turn whose start has been reached.
    for (let wi = 0; wi < words.length; wi += 1) {
      const start = Number(words[wi]?.start_ms);
      if (!Number.isFinite(start)) continue;
      if (currentTimeMs < start - off) break;
      best = { turnIdx: ti, wordIdx: wi };
    }
    // If the cursor has run past the entire turn (after its last word's
    // end_ms), keep walking — a later turn may also have started.
    // If the cursor is inside this turn (between firstStart and the
    // last word's end), `best` already points at the right word and
    // later turns won't have started yet.
    const lastWord = words[words.length - 1];
    const lastEnd = Number(lastWord?.end_ms);
    const turnEnd = Number(turn?.end_ms);
    const effectiveEnd = Number.isFinite(turnEnd) ? turnEnd : lastEnd;
    if (Number.isFinite(effectiveEnd) && currentTimeMs <= effectiveEnd - off) {
      // Cursor is inside this turn; the chosen `best` is final.
      break;
    }
    // Otherwise the cursor has passed this turn entirely — but the
    // gap-to-next-turn window should keep the LAST word of THIS turn
    // highlighted. Hold `best` and continue; if a later turn also
    // started, it'll overwrite. If not, this last word stays sticky.
  }
  return best;
}

const StructuredTurn = ({
  turn,
  turnIdx,
  activeWordIdx,
  onSeekToMs,
  turnRef,
  epochOffset = 0,
}) => {
  const role = turn?.role || "Other";
  const color = ROLE_COLOR[role] || "gray";
  const label = turn?.role_label || role;
  // Display + click-to-seek use conv-relative ms (subtract epochOffset).
  const startMs = Math.max(0, (Number(turn?.start_ms) || 0) - epochOffset);
  const words = Array.isArray(turn?.words) ? turn.words : [];

  return (
    <Flex
      ref={turnRef}
      gap={3}
      align="flex-start"
      data-testid="tagged-turn"
      data-turn-idx={turnIdx}
      data-role={role}
    >
      <Avatar
        name={role}
        size="sm"
        bg={`${color}.500`}
        color="white"
        flexShrink={0}
      />
      <Box flex={1} minW={0}>
        <Flex align="baseline" gap={2} mb={1}>
          <Text
            fontSize="xs"
            fontWeight="semibold"
            color={`${color}.600`}
            textTransform="uppercase"
            letterSpacing="wide"
          >
            {label}
          </Text>
          <Text
            fontSize="xs"
            color="gray.400"
            fontFamily="mono"
            data-testid="tagged-turn-timestamp"
          >
            {formatTimestamp(startMs)}
          </Text>
        </Flex>
        <Box
          bg="gray.50"
          borderRadius="lg"
          borderLeftWidth="3px"
          borderLeftColor={`${color}.300`}
          px={4}
          py={2}
        >
          <Text fontSize="sm" lineHeight="1.7" whiteSpace="pre-wrap">
            {words.map((w, wi) => {
              const isActive = wi === activeWordIdx;
              // Display attrs + click-to-seek both use conv-relative ms.
              const wordStart = Math.max(
                0,
                (Number(w?.start_ms) || 0) - epochOffset
              );
              const wordEnd = Math.max(
                0,
                (Number(w?.end_ms) || 0) - epochOffset
              );
              return (
                <React.Fragment key={wi}>
                  <Box
                    as="span"
                    cursor="pointer"
                    bg={isActive ? "yellow.200" : "transparent"}
                    borderRadius="sm"
                    px={isActive ? 1 : 0}
                    transition="background-color 100ms ease"
                    data-testid="tagged-word"
                    data-word-idx={wi}
                    data-start-ms={wordStart}
                    data-end-ms={wordEnd}
                    data-active={isActive ? "true" : "false"}
                    onClick={() => onSeekToMs?.(wordStart)}
                  >
                    {w?.text || ""}
                  </Box>
                  {wi < words.length - 1 ? " " : null}
                </React.Fragment>
              );
            })}
          </Text>
        </Box>
      </Box>
    </Flex>
  );
};

const TaggedTranscriptView = ({
  items,
  currentTimeMs = 0,
  onSeekToMs,
  // Epoch ms anchor of the conversation's audio (from the audio
  // manifest's `audio_start_epoch_ms`). Subtracted from every word/turn
  // start_ms before comparing to `currentTimeMs` (which is conv-relative).
  // Default 0 = no normalization (test mode + legacy data with
  // already-relative timestamps).
  audioStartEpochMs = 0,
  // Renderer for items that have no `data.turns` (legacy data). The
  // parent (ConversationDetailPage) passes its existing text-based
  // renderer so we don't duplicate that logic here.
  legacyRenderer,
}) => {
  const scrollPausedUntilRef = useRef(0);
  // Track the last turn we auto-scrolled to so we don't fight the user.
  const lastScrolledTurnRef = useRef(-1);
  const turnRefs = useRef([]);

  // Flatten across detail items so word/turn ordering is by chronological
  // start_ms — the player's currentTimeMs is conv-relative.
  const allTurns = useMemo(() => {
    const collected = [];
    if (!Array.isArray(items)) return collected;
    for (const item of items) {
      const data = item?.data || {};
      const turns = Array.isArray(data.turns) ? data.turns : null;
      if (!turns) continue;
      for (const t of turns) collected.push(t);
    }
    collected.sort(
      (a, b) => (Number(a?.start_ms) || 0) - (Number(b?.start_ms) || 0)
    );
    return collected;
  }, [items]);

  const active = useMemo(
    () => findActiveWord(allTurns, currentTimeMs, audioStartEpochMs),
    [allTurns, currentTimeMs, audioStartEpochMs]
  );

  // Pause auto-scroll for a few seconds after a user-initiated scroll.
  useEffect(() => {
    const onWheel = () => {
      scrollPausedUntilRef.current =
        Date.now() + SCROLL_PAUSE_AFTER_USER_INTERACTION_MS;
    };
    window.addEventListener("wheel", onWheel, { passive: true });
    window.addEventListener("touchmove", onWheel, { passive: true });
    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchmove", onWheel);
    };
  }, []);

  // Auto-scroll the active turn into view when it changes.
  useEffect(() => {
    if (!active) return;
    if (active.turnIdx === lastScrolledTurnRef.current) return;
    if (Date.now() < scrollPausedUntilRef.current) return;
    const el = turnRefs.current[active.turnIdx];
    if (el && typeof el.scrollIntoView === "function") {
      try {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
      } catch {
        /* jsdom doesn't implement smooth */
      }
    }
    lastScrolledTurnRef.current = active.turnIdx;
  }, [active]);

  // Detect items that have no `turns` — fall through to the legacy
  // renderer for those (per-item, so a partial roll-out still shows
  // structured turns where they exist).
  const sortedItems = Array.isArray(items)
    ? [...items].sort((a, b) => (a?.index ?? 0) - (b?.index ?? 0))
    : [];

  const hasAnyStructuredTurns = sortedItems.some(
    (item) => Array.isArray(item?.data?.turns) && item.data.turns.length > 0
  );

  if (!hasAnyStructuredTurns) {
    if (typeof legacyRenderer === "function") {
      return legacyRenderer(sortedItems);
    }
    return null;
  }

  // Track snippet-id transitions so we can interleave dashed
  // separators between snippets (Task 6a — same visual chrome the
  // RAW tab uses, just at turn granularity).
  let lastSnippetId = undefined;

  return (
    <VStack
      align="stretch"
      spacing={4}
      data-testid="tagged-transcript-structured"
    >
      {allTurns.map((turn, ti) => {
        const sid = snippetIdForTurn(turn);
        const showMarker = sid != null && sid !== lastSnippetId;
        lastSnippetId = sid;
        return (
          <React.Fragment key={ti}>
            {showMarker && <SnippetMarker snippetId={sid} />}
            <StructuredTurn
              turn={turn}
              turnIdx={ti}
              activeWordIdx={active?.turnIdx === ti ? active.wordIdx : -1}
              onSeekToMs={onSeekToMs}
              epochOffset={audioStartEpochMs}
              turnRef={(el) => {
                turnRefs.current[ti] = el;
              }}
            />
          </React.Fragment>
        );
      })}
    </VStack>
  );
};

export default TaggedTranscriptView;

// Exposed for testing — the active-word lookup is the contract surface.
export { findActiveWord };
